<template>
  <div v-if="order">
    <div class="d-flex align-items-start mt-3">
      <JobTypes :job="order" class="mr-2" />
      <h2 class="mb-0">
        <template v-if="jobTypeLabel(order)">
          {{ jobTypeLabel(order) }} {{ $t('at') }}
        </template>
        {{ order.customer && order.customer.name }}
        <template v-if="jobCustomerCity(order)">
          | {{ jobCustomerCity(order) }}
        </template>
        — {{ order.job.name }}
      </h2>
    </div>
    <div class="d-flex align-items-center mt-2 mb-4 text-muted">
      <img
        v-if="jobBrandLogo(order)"
        :src="jobBrandLogo(order)"
        :alt="order.brand.name"
        class="brand-logo mr-2"
      />
      <small>{{ brandDisplayName }}</small>
    </div>
    <b-alert
      v-if="completionMessage"
      show
      variant="success"
      class="mb-4 d-flex align-items-center"
    >
      <b-icon-check-circle-fill class="mr-2 flex-shrink-0" />
      <span>{{ completionMessage }}</span>
    </b-alert>
    <order-summary :order="order" :short="true">
      <template #actions>
        <b-row
          v-if="canCreateProposal"
          class="border-bottom py-3 py-lg-5 bg-light align-items-center"
        >
          <b-col cols="12" lg="3" class="text-muted">
            <h3 class="font-weight-light mb-3 mb-lg-0">
              {{ $t('order-details.actions') }}:
            </h3>
          </b-col>
          <b-col
            cols="12"
            lg="9"
            class="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center"
          >
            <b-button
              :to="{ name: 'create', params: { job_id } }"
              variant="primary"
              size="lg"
              class="text-uppercase px-5 mr-sm-2 mb-2 mb-sm-0 d-block d-sm-inline-block"
            >
              {{ $t('proposal.create') }}
            </b-button>
          </b-col>
        </b-row>
      </template>
    </order-summary>

    <!-- Accounting Documents Section -->
    <order-accounting-docs
      v-if="order && order.job"
      :job-id="order.job.api_id || order.job.sfid || job_id"
    />
  </div>
  <not-found v-else-if="order === false" />
</template>

<script>
/* eslint-disable vue/no-unused-components */
// eslint can't detect the dynamic usage of the components

import axios from 'axios';
import { mapGetters } from 'vuex';

import NotFound from '@/views/NotFound.vue';
import Summary from './Summary.vue';
import OrderAccountingDocs from './OrderAccountingDocs.vue';
import JobTypes from '@gid/vue-common/components/JobTypes.vue';
import jobDisplay from '@/mixins/jobDisplayMixin';

import { checkRouteAccess } from '@gid/vue-common/store/auth.module';
import { JobStatusEnum, UserRoleEnum } from '@gid/models';
import { BIconCheckCircleFill } from 'bootstrap-vue';

export default {
  mixins: [jobDisplay],
  components: {
    NotFound,
    'order-summary': Summary,
    OrderAccountingDocs,
    JobTypes,
    BIconCheckCircleFill,
  },
  props: {
    job_id: {
      type: String,
      required: true,
    },
  },
  watch: {
    job_id() {
      this.loadData();
    },
  },
  data() {
    return {
      order: null,
    };
  },
  computed: {
    ...mapGetters(['locale', 'user', 'isAuthenticated']),
    brandDisplayName() {
      return this.order?.opportunity?.name || '';
    },
    completionMessage() {
      const status = this.order?.job?.status;
      if (status === JobStatusEnum.APPROVAL_PENDING) {
        return this.$t('order-details.completion.documentation-done-in-review');
      }
      if (
        [JobStatusEnum.COMPLETED, JobStatusEnum.INVOICE_SENT].includes(status)
      ) {
        return this.$t('order-details.completion.order-completed');
      }
      return null;
    },
    canCreateProposal() {
      if (
        this.order.opportunity.can_create_proposal?.includes(
          UserRoleEnum.PARTNER,
        ) &&
        this.order.is_precheck &&
        this.order.job?.protocol_submitted &&
        this.order.last_sequence_index === this.order.sequence_index
      ) {
        const resolved = this.$router.resolve({ name: 'create' });
        return (
          resolved &&
          checkRouteAccess({
            route: resolved.route,
            user: this.user,
            isAuthenticated: this.isAuthenticated,
          })
        );
      }
      return false;
    },
  },
  created() {
    this.loadData();
  },
  methods: {
    loadData() {
      this.order = null;
      axios
        .get(`/api/partner/orders/${this.job_id}`)
        .then((response) => {
          this.order = response.data;
          this.$store.commit(
            'title',
            this.$t('order-with-number', { num: this.order.job.name }),
          );
        })
        .catch((error) => {
          if (error.response && error.response.status == 404) {
            this.order = false;
          }
        });
    },
  },
};
</script>

<style scoped>
.brand-logo {
  max-height: 24px;
  max-width: 120px;
  width: auto;
  height: auto;
  object-fit: contain;
}
</style>
